import React, { useEffect, useState } from 'react';
import usePickstarAccuracy from '../hooks/usePickstarAccuracy';
import './PickstarPredictionsViewer.css';

const PickstarPerformance = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const { loading, accuracyData, error, calculateAccuracy } = usePickstarAccuracy();

  // Format date to ET timezone
  const formatDateKey = (date) => {
    const nycDate = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const year = nycDate.getFullYear();
    const month = String(nycDate.getMonth() + 1).padStart(2, '0');
    const day = String(nycDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initialize with today's date
  useEffect(() => {
    const today = formatDateKey(new Date());
    setSelectedDate(today);
    calculateAccuracy(today);
  }, []);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    calculateAccuracy(e.target.value);
  };

  if (loading) {
    return (
      <div className="settlement-viewer">
        <div className="viewer-header">
          <h1>PickStar Performance Analysis</h1>
        </div>
        <div className="no-data">Calculating accuracy...</div>
      </div>
    );
  }

  return (
    <div className="settlement-viewer">
      <div className="viewer-header">
        <h1>PickStar Performance Analysis</h1>
        <div className="header-controls">
          <div className="date-selector">
            <label>Date (ET): </label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              max={formatDateKey(new Date())}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {accuracyData && (
        <>
          {/* Performance Summary */}
          <div className="stats-panel">
            <h2>Performance Summary for {accuracyData.date}</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Accuracy</span>
                <span className="stat-value" style={{
                  color: parseFloat(accuracyData.accuracy) >= 60 ? '#10b981' :
                         parseFloat(accuracyData.accuracy) >= 50 ? '#fbbf24' : '#ef4444'
                }}>
                  {accuracyData.accuracy}%
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Correct Predictions</span>
                <span className="stat-value success">{accuracyData.correct}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Incorrect Predictions</span>
                <span className="stat-value error">{accuracyData.incorrect}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Completed Games</span>
                <span className="stat-value">{accuracyData.completedGames}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Pending Games</span>
                <span className="stat-value">{accuracyData.pendingGames}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Confidence</span>
                <span className="stat-value">{accuracyData.avgConfidence.toFixed(1)}%</span>
              </div>
            </div>

            {/* Confidence Analysis */}
            <div className="stats-grid" style={{ marginTop: '20px' }}>
              <div className="stat-item">
                <span className="stat-label">Avg Confidence on Correct</span>
                <span className="stat-value success">
                  {accuracyData.avgCorrectConfidence.toFixed(1)}%
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Confidence on Incorrect</span>
                <span className="stat-value error">
                  {accuracyData.avgIncorrectConfidence.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Individual Game Results - Compact View */}
          <div className="games-container">
            <h2>Game-by-Game Analysis</h2>
            <div className="games-list" style={{ gap: '8px' }}>
              {accuracyData.results.map((result) => {
                const isCorrect = result.result?.correct;
                const isPending = result.status === 'pending';

                return (
                  <div
                    key={result.gameId}
                    className="game-card"
                    style={{
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderLeft: `3px solid ${
                        isCorrect ? '#10b981' :
                        isPending ? '#fbbf24' : '#ef4444'
                      }`,
                      background: isCorrect ? 'rgba(16, 185, 129, 0.03)' :
                                 isPending ? 'rgba(251, 191, 36, 0.03)' : 'rgba(239, 68, 68, 0.03)',
                      minHeight: '40px'
                    }}
                  >
                    {/* Teams and Prediction */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {/* Teams */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        minWidth: '250px'
                      }}>
                        <span style={{ color: '#10b981' }}>{result.prediction.teams.home.name}</span>
                        <span style={{ color: 'rgba(255,255,255,0.3)' }}>vs</span>
                        <span style={{ color: '#ef4444' }}>{result.prediction.teams.away.name}</span>
                      </div>

                      {/* Prediction */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        flex: 1
                      }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)' }}>Predicted:</span>
                        <strong style={{ color: '#fff' }}>{result.prediction.prediction?.winner}</strong>

                        {!isPending && result.result && (
                          <>
                            <span style={{ color: 'rgba(255,255,255,0.3)' }}>→</span>
                            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Actual:</span>
                            <strong style={{ color: isCorrect ? '#10b981' : '#ef4444' }}>
                              {result.result.actual}
                            </strong>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Right side: Score, Confidence, Status */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      {/* Score */}
                      {!isPending && result.result && (
                        <span style={{
                          fontSize: '12px',
                          color: 'rgba(255,255,255,0.5)',
                          minWidth: '40px',
                          textAlign: 'center'
                        }}>
                          {result.result.homeScore}-{result.result.awayScore}
                        </span>
                      )}

                      {/* Confidence */}
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: result.prediction.prediction?.confidence >= 70 ? 'rgba(16, 185, 129, 0.2)' :
                                   result.prediction.prediction?.confidence >= 40 ? 'rgba(251, 191, 36, 0.2)' :
                                   'rgba(239, 68, 68, 0.2)',
                        color: result.prediction.prediction?.confidence >= 70 ? '#10b981' :
                               result.prediction.prediction?.confidence >= 40 ? '#fbbf24' : '#ef4444',
                        minWidth: '45px',
                        textAlign: 'center'
                      }}>
                        {result.prediction.prediction?.confidence?.toFixed(1)}%
                      </span>

                      {/* Status */}
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '700',
                        background: isCorrect ? 'rgba(16, 185, 129, 0.2)' :
                                   isPending ? 'rgba(251, 191, 36, 0.2)' :
                                   'rgba(239, 68, 68, 0.2)',
                        color: isCorrect ? '#10b981' :
                               isPending ? '#fbbf24' : '#ef4444',
                        minWidth: '70px',
                        textAlign: 'center'
                      }}>
                        {isPending ? '⏳ PENDING' : isCorrect ? '✓ CORRECT' : '✗ WRONG'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PickstarPerformance;